import React, {useEffect, useRef, useState} from 'react';
import {Alignment, Fit, Layout, useRive, useStateMachineInput} from '@rive-app/react-canvas';
import PropTypes from 'prop-types';
import {phaseNamesMapper} from '../../../utils/ftemConstants';
import {phaseTypes, subPhaseTypes} from '@swiss-ski/sski-entity-types';
import InfoCard from '../InfoCard';

// Custom Hook to manage Rive state machine inputs
const useRiveInputs = (rive, stateMachineName) => {
    // Define all useStateMachineInput calls at the top level
    const hoverF = useStateMachineInput(rive, stateMachineName, 'isHoverF');
    const hoverT = useStateMachineInput(rive, stateMachineName, 'isHoverT');
    const hoverE = useStateMachineInput(rive, stateMachineName, 'isHover-E');
    const hoverM = useStateMachineInput(rive, stateMachineName, 'isHover-M');
    const selectF = useStateMachineInput(rive, stateMachineName, 'isClicked');
    const selectT = useStateMachineInput(rive, stateMachineName, 'isClickedT');
    const selectE = useStateMachineInput(rive, stateMachineName, 'isClicked-E');
    const selectM = useStateMachineInput(rive, stateMachineName, 'isClicked-M');
    const isSelectedF1 = useStateMachineInput(rive, stateMachineName, 'IsSelected-f1');
    const isSelectedF2 = useStateMachineInput(rive, stateMachineName, 'IsSelected-f2');
    const isSelectedF3 = useStateMachineInput(rive, stateMachineName, 'IsSelected-f3');
    const isSelectedT1 = useStateMachineInput(rive, stateMachineName, 'isSelected-t1');
    const isSelectedT2 = useStateMachineInput(rive, stateMachineName, 'isSelected-t2');
    const isSelectedT3 = useStateMachineInput(rive, stateMachineName, 'isSelected-t3');
    const isSelectedT4 = useStateMachineInput(rive, stateMachineName, 'isSelected-t4');
    const isSelectedE1 = useStateMachineInput(rive, stateMachineName, 'isSelected-e1');
    const isSelectedE2 = useStateMachineInput(rive, stateMachineName, 'isSelected-e2');
    const isSelectedM1 = useStateMachineInput(rive, stateMachineName, 'isSelected-m');

    // Create inputs object
    return {
        hoverF,
        hoverT,
        hoverE,
        hoverM,
        selectF,
        selectT,
        selectE,
        selectM,
        isSelectedF1,
        isSelectedF2,
        isSelectedF3,
        isSelectedT1,
        isSelectedT2,
        isSelectedT3,
        isSelectedT4,
        isSelectedE1,
        isSelectedE2,
        isSelectedM1,
    };
};

const InfoCards = ({hoveredPhase, selectedPhase, introductionContents, selectedSubphase, isMobile, sortDetails, setHoveredPhase}) => {
    return (
        ((hoveredPhase && selectedPhase && introductionContents) || selectedSubphase) && (
            <div
                className={`${
                    hoveredPhase === phaseTypes.MASTERY && !selectedSubphase
                        ? 'is-flex is-justify-content-center is-align-items-center is-flex-direction-row-reverse'
                        : ''
                } ${isMobile ? 'sski-c-dashboard-screen__mobile-legend-container' : ''} sski-c-dashboard-screen__legend-container--${
                    selectedSubphase ? 'subphase' : hoveredPhase.toLowerCase()
                }`}
            >
                {!selectedSubphase && !isMobile && (
                    <div className={`sski-c-dashboard-screen__legend-line--${hoveredPhase.toLowerCase()}`} />
                )}
                {selectedSubphase && hoveredPhase ? (
                    sortDetails.length > 0 ? null : null
                ) : hoveredPhase ? (
                    <InfoCard
                        hoveredPhase={hoveredPhase}
                        selectedPhase={selectedPhase}
                        selectedPhaseTitle={introductionContents[Object.values(subPhaseTypes[hoveredPhase])[0]]?.text || ''}
                        isMobile={isMobile}
                        closeCard={setHoveredPhase}
                    />
                ) : null}
            </div>
        )
    );
};


const FTEMPillarsRiveAnimation = ({
    onHover,
    onClick,
    hoveredPhase,
    selectedPhase,
    introductionContents,
    selectedSubphase,
    isMobile,
    sortDetails,
    setHoveredPhase,
    clickedPhase,
    setSelectedPhase,
}) => {
    const STATE_MACHINE_NAME = 'FTEM Navigator';
    const {RiveComponent, rive} = useRive({
        src: `${process.env.PUBLIC_URL}/assets/animation/ftem_navigation_remastered.riv`,
        stateMachines: STATE_MACHINE_NAME,
        artboard: STATE_MACHINE_NAME,
        autoplay: true,
        layout: new Layout({
            fit: Fit.Contain,
            alignment: Alignment.BottomCenter,
        }),
    });
    const [isTClicked, setIsTClicked] = useState(false);
    const timeoutRefs = useRef({F: null, T: null, E: null, M: null});

    // Use custom Hook to initialize state machine inputs
    const inputs = useRiveInputs(rive, STATE_MACHINE_NAME);

    useEffect(() => {
        if (rive) {
            rive.on('statechange', event => {
                const currentStateChangeName = event.data.length === 1 ? event.data[0] : null;
                if (!currentStateChangeName) return;
                const splitStateChangeName = currentStateChangeName.split('-');
                const lastSplitStateChangeNameElement = splitStateChangeName[splitStateChangeName.length - 1];
                const formatM = splitStateChangeName[0] === 'm1' ? 'm' : splitStateChangeName[0];
                const allEvents = rive?.prevAnim;
                const isHoverLast = allEvents?.includes('hover');
                const prefixLast = allEvents?.split('-')[0];
                const isIdleNow = currentStateChangeName?.includes('idle');

                if (currentStateChangeName === 'T-clicked' && !isTClicked) {
                    setIsTClicked(true);
                } else {
                    setIsTClicked(false);
                }

                if (isHoverLast && isIdleNow && prefixLast !== splitStateChangeName[0]) {
                    return;
                }
                rive.prevAnim = event.data[0];

                if (lastSplitStateChangeNameElement === 'hover') {
                    onHover(phaseNamesMapper[splitStateChangeName[0].toUpperCase()]);
                    onClick('');
                }

                if (lastSplitStateChangeNameElement === 'idle') {
                    onHover('');
                    onClick('');
                    setSelectedPhase('');
                }

                if (lastSplitStateChangeNameElement === 'clicked') {
                    onClick(formatM);
                }
            });
        }
    }, [rive, onHover, onClick, setSelectedPhase, isTClicked]);

    const handleClick = isSubPhase => {
        if (rive) {
            const subPhaseMap = {
                f1: inputs.isSelectedF1,
                f2: inputs.isSelectedF2,
                f3: inputs.isSelectedF3,
                t1: inputs.isSelectedT1,
                t2: inputs.isSelectedT2,
                t3: inputs.isSelectedT3,
                t4: inputs.isSelectedT4,
                e1: inputs.isSelectedE1,
                e2: inputs.isSelectedE2,
                m1: inputs.isSelectedM1,
            };

            if (subPhaseMap[isSubPhase]) {
                subPhaseMap[isSubPhase].fire();
            } else if (isSubPhase === hoveredPhase) {
                const getName = hoveredPhase?.charAt(0);
                const phaseMap = {
                    F: inputs.selectF,
                    T: inputs.selectT,
                    E: inputs.selectE,
                    M: inputs.selectM,
                };
                if (phaseMap[getName]) {
                    phaseMap[getName].fire();
                }
            }
        }
    };

    const handleMouseLeave = (phaseKey, hoverInput) => {
        if (timeoutRefs.current[phaseKey]) {
            clearTimeout(timeoutRefs.current[phaseKey]);
        }
        timeoutRefs.current[phaseKey] = setTimeout(() => {
            if (hoverInput) {
                hoverInput.value = false;
            }
        }, 150);
    };

    const handleMouseEnter = (phaseKey, hoverInput) => {
        if (timeoutRefs.current[phaseKey]) {
            clearTimeout(timeoutRefs.current[phaseKey]);
        }
        if (hoverInput) {
            hoverInput.value = true;
        }
    };

    return (
        <div className="is-flex is-justify-content-center">
            <div className="is-flex" style={{position: 'relative', width: '80vw', top: '40vh', aspectRatio: '2.6 / 1'}}>
                {clickedPhase && (
                    <InfoCards
                        hoveredPhase={hoveredPhase}
                        selectedPhase={selectedPhase}
                        introductionContents={introductionContents}
                        selectedSubphase={selectedSubphase}
                        isMobile={isMobile}
                        sortDetails={sortDetails}
                        setHoveredPhase={setHoveredPhase}
                    />
                )}
                <RiveComponent />
                {hoveredPhase === 'FOUNDATION' && (
                    <div>
                        <div className="subphase-pillar f1-pillar" onClick={() => handleClick('f1')} />
                        <div className="subphase-pillar f2-pillar" onClick={() => handleClick('f2')} />
                        <div className="subphase-pillar f3-pillar" onClick={() => handleClick('f3')} />
                    </div>
                )}
                {hoveredPhase === 'TALENT' && (
                    <div>
                        <div className="subphase-pillar t1-pillar" onClick={() => handleClick('t1')} />
                        <div className="subphase-pillar t2-pillar" onClick={() => handleClick('t2')} />
                        <div className="subphase-pillar t3-pillar" onClick={() => handleClick('t3')} />
                        <div className="subphase-pillar t4-pillar" onClick={() => handleClick('t4')} />
                    </div>
                )}
                {hoveredPhase === 'ELITE' && (
                    <div>
                        <div className="subphase-pillar e1-pillar" onClick={() => handleClick('e1')} />
                        <div className="subphase-pillar e2-pillar" onClick={() => handleClick('e2')} />
                    </div>
                )}
                {hoveredPhase === 'MASTERY' && (
                    <div>
                        <div className="subphase-pillar m1-pillar" onClick={() => handleClick('m1')} />
                    </div>
                )}
                {!clickedPhase && (
                    <div
                        className={`subphase-pillar f-pillar ${hoveredPhase === 'FOUNDATION' ? 'hover' : ''}`}
                        onClick={() => handleClick(hoveredPhase)}
                        onMouseEnter={() => handleMouseEnter('F', inputs.hoverF)}
                        onMouseLeave={() => handleMouseLeave('F', inputs.hoverF)}
                    />
                )}
                {!clickedPhase && (
                    <div
                        className={`subphase-pillar t-pillar ${hoveredPhase === 'TALENT' ? 'hover' : ''}`}
                        onClick={() => handleClick(hoveredPhase)}
                        onMouseEnter={() => handleMouseEnter('T', inputs.hoverT)}
                        onMouseLeave={() => handleMouseLeave('T', inputs.hoverT)}
                    />
                )}
                {!clickedPhase && (
                    <div
                        className={`subphase-pillar e-pillar ${hoveredPhase === 'ELITE' ? 'hover' : ''}`}
                        onClick={() => handleClick(hoveredPhase)}
                        onMouseEnter={() => handleMouseEnter('E', inputs.hoverE)}
                        onMouseLeave={() => handleMouseLeave('E', inputs.hoverE)}
                    />
                )}
                {!clickedPhase && (
                    <div
                        className={`subphase-pillar m-pillar ${hoveredPhase === 'MASTERY' ? 'hover' : ''}`}
                        onClick={() => handleClick(hoveredPhase)}
                        onMouseEnter={() => handleMouseEnter('M', inputs.hoverM)}
                        onMouseLeave={() => handleMouseLeave('M', inputs.hoverM)}
                    />
                )}
            </div>
        </div>
    );
};

FTEMPillarsRiveAnimation.propTypes = {
    onHover: PropTypes.func.isRequired,
    onClick: PropTypes.func.isRequired,
    hoveredPhase: PropTypes.string,
    selectedPhase: PropTypes.object,
    introductionContents: PropTypes.object,
    selectedSubphase: PropTypes.object,
    isMobile: PropTypes.bool,
    sortDetails: PropTypes.array,
    setHoveredPhase: PropTypes.func,
    clickedPhase: PropTypes.string,
    setSelectedPhase: PropTypes.func,
};

FTEMPillarsRiveAnimation.defaultProps = {
    hoveredPhase: '',
    selectedPhase: null,
    introductionContents: {},
    selectedSubphase: null,
    isMobile: false,
    sortDetails: [],
    setHoveredPhase: () => {},
    clickedPhase: '',
    setSelectedPhase: () => {},
};

export default FTEMPillarsRiveAnimation;
